<template>
  <v-card
    width="400px"
    class="enhanced-network-card"
    hover
  >
    <!-- Header Section - Clickable -->
    <v-card-text
      class="cursor-pointer"
      @click="selectReport"
    >
      <v-col>
        <v-row>
          <v-col class="text-truncate">
            <span
              class="font-weight-bold primary--text"
              :title="report.text"
            >{{ report.text }}</span>
          </v-col>
        </v-row>

        <v-row
          v-if="isColumnVisible('billingCode')"
          class="mt-0"
        >
          <v-col class="pt-0">
            <span class="caption font-weight-bold">Billing Code </span>
            <span class="caption font-weight-medium">{{ report.billingCode }}</span>
          </v-col>
        </v-row>

        <v-row
          v-if="isColumnVisible('networkCode')"
          class="mt-0"
        >
          <v-col class="pt-0">
            <span class="caption font-weight-bold">Network </span>
            <span class="caption font-weight-medium">{{ report.networkCode }}</span>
          </v-col>
        </v-row>

        <v-row
          v-if="report.teamId && isColumnVisible('teamId')"
          class="mt-0"
        >
          <v-col class="pt-0">
            <span class="caption font-weight-bold">Team ID</span>
            <span class="caption font-weight-medium pl-1">{{ report.teamId === '-1' ? 'All' : report.teamId }}</span>
          </v-col>
        </v-row>
      </v-col>
    </v-card-text>

    <v-divider />

    <!-- Tab Metrics Section -->
    <v-card-text class="pb-2 pt-3 px-6">
      <div class="caption font-weight-bold mb-2 gray--text">
        Quick Metrics
      </div>

      <!-- Tab Metric Rows - Each clickable -->
      <v-row
        v-for="tab in tabMetrics"
        :key="tab.id"
        class="ma-0 mb-1 tab-metric-row align-center cursor-pointer"
        @click="selectReportWithTab(tab.id)"
      >
        <v-col class="col-auto pa-0 pr-2">
          <font-awesome-icon
            v-if="tab.icon"
            :icon="tab.icon"
            :class="getMetricIconClass(tab)"
            size="sm"
            fixed-width
          />
        </v-col>
        <v-col class="pa-0 caption">
          <span class="font-weight-medium">{{ tab.label }}</span>
        </v-col>
        <v-spacer />
        <v-col class="col-auto pa-0">
          <v-chip
            x-small
            :color="getMetricChipColor(tab)"
            :text-color="getMetricChipTextColor(tab)"
            :outlined="tab.count === 0"
            class="metric-chip"
          >
            {{ tab.count }}
          </v-chip>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script>
import filter from 'src/utils/filter'
import summaries from 'src/delivery/store/utils/summaries'

export default {
  name: 'EnhancedNetworkCard',
  props: {
    report: {
      type: Object,
      required: true,
    },
    tabConfigs: {
      type: Array,
      required: true,
    },
    networkData: {
      type: Array,
      default: () => [],
    },
    reportPath: {
      type: String,
      default: '/reports/pacing',
    },
    /**
     * Array of column value strings that are currently visible.
     * When empty or not provided, all columns are shown (default behavior).
     * Supported values: 'billingCode', 'networkCode', 'teamId'
     */
    visibleColumns: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    tabMetrics() {
      // Calculate counts for each tab based on network data
      return this.tabConfigs.map((tab) => {
        const count = this.calculateTabCount(tab)
        return {
          ...tab,
          count,
        }
      })
    },
  },
  methods: {
    /**
     * Check if a column/property is visible on the card.
     * When visibleColumns is empty (default), all columns are visible.
     * @param {String} columnValue - The column identifier (e.g., 'billingCode', 'networkCode', 'teamId')
     * @returns {Boolean}
     */
    isColumnVisible(columnValue) {
      if (!this.visibleColumns || this.visibleColumns.length === 0) return true
      return this.visibleColumns.includes(columnValue)
    },

    calculateTabCount(tab) {
      if (!this.networkData || this.networkData.length === 0) {
        return 0
      }

      // If it's the "all" tab, return total count
      if (tab.id === 'all' || tab.id === 'all-networks') {
        return this.networkData.length
      }

      // If tab has a summaryId, apply filter logic
      if (tab.summaryId) {
        // For Discrepancy data: use simple percentage matching
        if (this.networkData[0]?.discrepancyPercentage !== undefined) {
          return this.networkData.filter((item) => this.matchesDiscrepancySummary(item.discrepancyPercentage, tab.summaryId)).length
        }

        // For Pacing data: apply summary filter parameters
        const pacingSummaries = summaries?.pacing || summaries?.default?.pacing
        const summaryConfig = pacingSummaries?.[tab.summaryId]

        if (!summaryConfig || !summaryConfig.parameters) {
          return 0
        }

        // Convert summary parameters to filter format (same logic as Pacing.vue)
        const filterData = {}
        const paramKeys = Object.keys(summaryConfig.parameters)

        paramKeys.forEach((key) => {
          const paramValue = summaryConfig.parameters[key]
          filterData[key] = typeof paramValue === 'function' ? paramValue() : paramValue
        })

        const filterKeys = Object.keys(filterData)

        if (filterKeys.length === 0) {
          return 0
        }

        // Apply filters using the same filter utility as Pacing.vue
        const reportFilter = filter.report.bind(filter)
        const isItemIncluded = (item) => reportFilter(item, filterData, filterKeys)

        return this.networkData.filter(isItemIncluded).length
      }

      return 0
    },

    matchesDiscrepancySummary(discrepancyPercentage, summaryId) {
      const value = parseFloat(discrepancyPercentage)

      if (Number.isNaN(value)) {
        return false
      }

      switch (summaryId) {
        case 'HIGH_POSITIVE':
          return value > 10
        case 'BETWEEN_POSITIVE_ONE_AND_TEN':
          return value >= 1 && value <= 10
        case 'APPROXIMATELY_ZERO':
          return value > -1 && value < 1
        case 'BETWEEN_NEGATIVE_ONE_AND_TEN':
          return value <= -1 && value >= -10
        case 'HIGH_NEGATIVE':
          return value < -10
        default:
          return false
      }
    },

    getMetricIconClass(tab) {
      const { count, summaryId, chipColor } = tab

      // No issues
      if (count === 0) {
        return 'gray--text'
      }

      // Pacing categories
      const warningCategories = ['MISSING_CREATIVES', 'ZERO_IMPRESSIONS', 'STUCK_IN_PAUSED', 'STUCK_IN_READY']
      const criticalCategories = ['AT_RISK']

      // Discrepancy categories
      const discrepancyCritical = ['HIGH_POSITIVE', 'HIGH_NEGATIVE']
      const discrepancyWarning = ['BETWEEN_POSITIVE_ONE_AND_TEN', 'BETWEEN_NEGATIVE_ONE_AND_TEN']
      const discrepancySuccess = ['APPROXIMATELY_ZERO']

      if (criticalCategories.includes(summaryId) || discrepancyCritical.includes(summaryId)) {
        return 'error--text'
      }

      if (warningCategories.includes(summaryId) || discrepancyWarning.includes(summaryId)) {
        return 'warning--text'
      }

      if (discrepancySuccess.includes(summaryId)) {
        return 'success--text'
      }

      // Fallback to chipColor if available
      if (chipColor) {
        return `${chipColor}--text`
      }

      return 'primary--text'
    },

    getMetricChipColor(tab) {
      const { count, summaryId, chipColor } = tab

      // No issues
      if (count === 0) {
        return 'gray'
      }

      // Pacing categories
      const warningCategories = ['AT_RISK', 'MISSING_CREATIVES', 'ZERO_IMPRESSIONS', 'STUCK_IN_READY']
      const criticalCategories = ['STUCK_IN_PAUSED']

      // Discrepancy categories
      const discrepancyCritical = ['HIGH_POSITIVE', 'HIGH_NEGATIVE']
      const discrepancyWarning = ['BETWEEN_POSITIVE_ONE_AND_TEN', 'BETWEEN_NEGATIVE_ONE_AND_TEN']

      if (criticalCategories.includes(summaryId) || discrepancyCritical.includes(summaryId)) {
        return 'error'
      }

      if (warningCategories.includes(summaryId) || discrepancyWarning.includes(summaryId)) {
        return 'warning'
      }

      if (tab.id === 'all' || tab.id === 'all-networks') {
        return 'coolgray'
      }

      return chipColor || 'primary'
    },

    getMetricChipTextColor(tab) {
      if (tab.count === 0) return undefined
      const color = this.getMetricChipColor(tab)
      if (color === 'coolgray' || color === 'error' || color === 'primary') return 'white'
      return undefined
    },

    async selectReport() {
      // Build query params for network selection
      const { billingCode, teamId } = this.report
      const query = {
        billingCode,
        teamId: String(teamId),
      }

      // Navigate to report with query params
      return this.$router.push({
        path: this.reportPath,
        query,
      })
    },

    async selectReportWithTab(tabId) {
      // Build query params for network selection and tab filter
      const { billingCode, teamId } = this.report
      const query = {
        billingCode,
        teamId: String(teamId),
      }

      // Add tab filter if not viewing all
      if (tabId !== 'all-networks') {
        query.tab = tabId
      }

      // eslint-disable-next-line no-console
      console.info('🎯 [ROUTE-DEBUG] EnhancedNetworkCard.selectReportWithTab', {
        tabId,
        billingCode,
        teamId,
        query,
        stackTrace: new Error().stack,
      })

      // Navigate to report with query params
      return this.$router.push({
        path: this.reportPath,
        query,
      })
    },
  },
}
</script>

<style scoped lang="scss">
.enhanced-network-card {
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
  }
}

.tab-metric-row {
  padding: 4px 0;
  border-radius: 4px;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba(0, 0, 0, 0.03);
  }
}

.theme--dark .tab-metric-row:hover {
  background-color: rgba(255, 255, 255, 0.05);
}

.metric-chip {
  min-width: 40px;
  justify-content: center;
  font-weight: 600;
}
</style>
